import { TeethSelect, type ToothObject } from "@jasper/forms";
import { useForm } from "@tanstack/react-form";

import type { ConfigurableProduct, ProductStepProps } from "../types";
import {
  adjacentTeethSchema,
  ItemScopeEnum,
  ItemTypeEnum,
} from "@jasper/client-models";
import type { ProductItemArgs, UpdateProductArgs } from "@jasper/use-cases";
import { useTranslation } from "react-i18next";
import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useTeethSelectionSubmission } from "./useTeethSelectionSubmission";
import { useRegisterStepSave } from "../useRegisterStepSave";
import {
  Button,
  Dialog,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useProductTranslations } from "@jasper/translations";
import { useTeethSelectionDescription } from "./useTeethSelectionDescription";
import { useProductGroup } from "../../hooks/useProductGroup";
import { usePreselectedTeeth } from "../../hooks/usePreselectedTeeth";

type Props = ProductStepProps;

const toToothNumbers = (teeth: ToothObject[]) =>
  teeth.map(({ tooth }) => tooth);

const getAdjacencyError = (teeth: number[]) =>
  adjacentTeethSchema.safeParse(teeth).error?.issues[0]?.message;

const getBridgeError = (bridge: ToothObject[]) =>
  getAdjacencyError(toToothNumbers(bridge)) ??
  (bridge.some(({ value }) => value === ItemTypeEnum.abutment)
    ? undefined
    : "MISSING_IMPLANTED_TOOTH");

const validateSelection = ({
  value,
  setTeethInError,
}: {
  value: {
    teeth: ToothObject[];
    groupedTeeth: ToothObject[][];
  };
  setTeethInError: Dispatch<SetStateAction<number[]>>;
}): Record<string, unknown> | undefined => {
  setTeethInError([]);

  const teeth = toToothNumbers(value.teeth);
  const bridges = value.groupedTeeth;

  const groupedTeeth = toToothNumbers(bridges.flat());
  if (
    groupedTeeth.length !== teeth.length ||
    groupedTeeth.some(tooth => !teeth.includes(tooth))
  ) {
    return { teeth: "unexpected" };
  }

  // When no teeth are grouped, validate the whole selection to fall back on default error messages.
  if (bridges.every(bridge => bridge.length === 1)) {
    const error = getAdjacencyError(teeth);
    if (error) return { teeth: error };
  }

  const invalidBridges = bridges
    .map(bridge => ({ bridge, error: getBridgeError(bridge) }))
    .filter(({ error }) => error);

  if (!invalidBridges.length) return undefined;

  setTeethInError(
    invalidBridges.flatMap(({ bridge }) => toToothNumbers(bridge))
  );
  return { teeth: invalidBridges[0].error };
};

const applyTeethSelection = ({
  teeth,
  product,
}: {
  teeth: ToothObject[];
  product: ConfigurableProduct;
}): Promise<UpdateProductArgs> | UpdateProductArgs => {
  const teethNumber = teeth.map(({ tooth }) => tooth);

  const itemsToSave: ProductItemArgs[] = teeth.flatMap<ProductItemArgs>(
    ({ tooth, value }) => {
      const existingItems = product.items.filter(
        item => item.scope === ItemScopeEnum.tooth && item.tooth === tooth
      );

      const existingPonticBridge = existingItems.find(
        item => item.type === ItemTypeEnum.bridgePontic
      );

      const existingImplantBridge = existingItems.find(
        item => item.type === ItemTypeEnum.bridgeImplant
      );

      if (value === ItemTypeEnum.bridgePontic) {
        return [
          {
            tooth,
            scope: ItemScopeEnum.tooth,
            type: ItemTypeEnum.bridgePontic,
            materialCharacteristics:
              existingPonticBridge?.materialCharacteristics ??
              existingImplantBridge?.materialCharacteristics,
          },
        ];
      }

      const globalAbutmentCharacteristics = product.items.find(
        item =>
          !!item.implantCharacteristics &&
          !!item.abutmentCharacteristics &&
          (item.type === ItemTypeEnum.abutment ||
            item.type === ItemTypeEnum.customAbutment)
      );

      const globalImplantedItemCharacteristics = product.items.find(
        item => !!item.implantedItemCharacteristics
      );

      return [
        {
          tooth,
          scope: ItemScopeEnum.tooth,
          type: globalAbutmentCharacteristics?.type ?? ItemTypeEnum.abutment,
          implantCharacteristics:
            globalAbutmentCharacteristics?.implantCharacteristics,
          abutmentCharacteristics:
            globalAbutmentCharacteristics?.abutmentCharacteristics,
        },
        {
          tooth,
          scope: ItemScopeEnum.tooth,
          type: ItemTypeEnum.bridgeImplant,
          materialCharacteristics:
            existingImplantBridge?.materialCharacteristics ??
            existingPonticBridge?.materialCharacteristics,
          implantedItemCharacteristics:
            existingImplantBridge?.implantedItemCharacteristics ??
            globalImplantedItemCharacteristics?.implantedItemCharacteristics,
        },
      ];
    }
  );

  return {
    productId: product.id,
    teeth: teethNumber,
    items: itemsToSave,
  };
};

const getTeethObjects = (product: ConfigurableProduct): ToothObject[] => {
  return product.teeth.map(tooth => {
    const toothItems = product.items.filter(
      item => item.scope === ItemScopeEnum.tooth && item.tooth === tooth
    );

    if (
      toothItems.some(
        ({ type }) =>
          type === ItemTypeEnum.abutment || type === ItemTypeEnum.customAbutment
      )
    ) {
      return { tooth, value: ItemTypeEnum.abutment };
    }

    return { tooth, value: ItemTypeEnum.bridgePontic };
  });
};

export const ImplantBridgeTeethStep = ({
  stepKey,
  products,
  itemType,
  orderConfiguration,
  registerStepSave,
  goToStep,
}: Props) => {
  // Teeth of the invalid bridges only, so that the valid ones are not shown in error.
  const [teethInError, setTeethInError] = useState<number[]>([]);

  const { t } = useTranslation("product-workflows", {
    keyPrefix: "steps.teeth",
  });
  const { t: translateProduct } = useProductTranslations();
  const describeTeethSelection = useTeethSelectionDescription();
  const theme = useMantineTheme();

  const { groupProducts } = useProductGroup(products);
  const preselectedTeeth = usePreselectedTeeth();

  const initialGroupedTeeth = useMemo(
    () => groupProducts.map(getTeethObjects),
    [groupProducts]
  );

  const initialTeeth = useMemo(
    () =>
      preselectedTeeth.length
        ? preselectedTeeth.map(tooth => ({
            tooth,
            value: ItemTypeEnum.abutment,
          }))
        : initialGroupedTeeth.flat(),
    [preselectedTeeth, initialGroupedTeeth]
  );

  const submitTeethSelection = useTeethSelectionSubmission({
    products,
    itemType,
    orderConfiguration,
    formatTeethSelection: applyTeethSelection,
  });

  const defaultValues = useMemo(
    () => ({
      product: {
        teeth: [...initialTeeth],
        groupedTeeth: [...initialGroupedTeeth],
      },
    }),
    [initialTeeth, initialGroupedTeeth]
  );

  const [isPonticWarningOpen, setIsPonticWarningOpen] = useState(
    !!initialTeeth.length &&
      (initialTeeth.every(tooth => tooth.value !== ItemTypeEnum.bridgePontic) ||
        initialGroupedTeeth.some(group =>
          group.every(tooth => tooth.value !== ItemTypeEnum.bridgePontic)
        ))
  );

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const everyBridgeHavePontic = value.product.groupedTeeth.every(group =>
        group.some(tooth => tooth.value === ItemTypeEnum.bridgePontic)
      );
      setIsPonticWarningOpen(!everyBridgeHavePontic);

      await submitTeethSelection(value.product.groupedTeeth);
    },
    listeners: {
      onChangeDebounceMs: 250,
      onBlurDebounceMs: 250,
      onChange: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
      onBlur: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
    },
  });

  useRegisterStepSave({
    stepKey,
    registerStepSave,
    form,
  });

  const handleToothClick = ({
    toothId,
    selectedTeeth,
    setSelectedTeeth,
  }: {
    toothId: number;
    selectedTeeth: ToothObject[];
    setSelectedTeeth: (teeth: ToothObject[]) => void;
  }) => {
    const alreadySelectedTooth = selectedTeeth.find(
      ({ tooth }) => tooth === toothId
    );

    if (!alreadySelectedTooth) {
      setSelectedTeeth([
        ...selectedTeeth,
        { tooth: toothId, value: ItemTypeEnum.abutment },
      ]);

      return;
    }

    if (alreadySelectedTooth.value === ItemTypeEnum.abutment) {
      setSelectedTeeth([
        ...selectedTeeth.filter(({ tooth }) => tooth !== toothId),
        { tooth: toothId, value: ItemTypeEnum.bridgePontic },
      ]);

      return;
    }

    setSelectedTeeth(selectedTeeth.filter(({ tooth }) => tooth !== toothId));
  };

  return (
    <Stack h="100%" mih={0} p="md" gap="md">
      <Dialog opened={isPonticWarningOpen}>
        <Stack>
          <Stack gap="s1">
            <Title order={4} c="warning">
              Aucun pontique dans ce bridge
            </Title>
            <Text size="xs">
              Vérifiez qu’aucune dent n’est à remplacer avant de continuer.
            </Text>
          </Stack>
          <Button onClick={goToStep} variant="light">
            Modifier la séléction
          </Button>
        </Stack>
      </Dialog>
      <Stack gap="md" flex={1} mih={0}>
        <Text c="dimmed" size="sm">
          {t("bulk.hint")}
        </Text>
        <form.Field
          name="product"
          listeners={{
            onChange: ({ fieldApi }) => {
              fieldApi.setErrorMap({
                onSubmit: undefined,
                onBlur: undefined,
              });
            },
          }}
          validators={{
            onChange: ({ value, fieldApi }) => {
              const res = validateSelection({
                value,
                setTeethInError,
              });

              if (!res) {
                fieldApi.setErrorMap({
                  onBlur: undefined,
                  onSubmit: undefined,
                });
              }

              return res;
            },

            onBlur: ({ value }) =>
              validateSelection({
                value,
                setTeethInError,
              }),
            onSubmit: ({ value }) =>
              validateSelection({
                value,
                setTeethInError,
              }),
          }}
        >
          {field => {
            const formErrors =
              field.state.meta.errorMap.onBlur ??
              field.state.meta.errorMap.onSubmit ??
              field.state.meta.errorMap.onChange;

            const errors =
              typeof formErrors?.teeth === "string"
                ? t(formErrors?.teeth, {
                    keyPrefix: "steps.teeth.errors",
                    defaultValue: t("errorOccurred", { keyPrefix: "common" }),
                  })
                : undefined;

            const getTeethInError = () => {
              if (!errors) {
                return [];
              }

              if (teethInError.length) {
                return teethInError;
              }

              return field.state.value.teeth.map(({ tooth }) => tooth);
            };

            return (
              <TeethSelect
                onBlur={() => field.handleBlur()}
                teeth={field.state.value.teeth}
                groupedTeeth={field.state.value.groupedTeeth.map(group =>
                  group.map(({ tooth }) => tooth)
                )}
                error={errors}
                teethError={getTeethInError()}
                withGrouping
                groupAdjacentByDefault
                toothTooltip={toothId => {
                  const selectedTooth = field.state.value.teeth.some(
                    ({ tooth }) => tooth === toothId
                  );

                  if (!selectedTooth) {
                    return "Double-clic pour pontique";
                  }
                  if (
                    field.state.value.teeth.some(
                      ({ tooth, value }) =>
                        tooth === toothId && value === ItemTypeEnum.bridgePontic
                    )
                  ) {
                    return "Pontique";
                  }

                  return "Implanto-portée";
                }}
                additionalSection={
                  <Group>
                    <Group gap="s1">
                      <Paper w={20} h={14} bg="brand" radius="xs" />
                      <Text variant="caption">
                        {translateProduct(ItemTypeEnum.abutment, {
                          count: 1,
                        })}
                      </Text>
                    </Group>

                    <Group gap="s1">
                      <Paper
                        w={20}
                        h={14}
                        bd={`dashed 1px ${theme.colors.brand[6]}`}
                        bg="brand.2"
                        radius="xs"
                      />
                      <Text variant="caption">
                        {translateProduct(ItemTypeEnum.bridgePontic, {
                          count: 1,
                        })}
                      </Text>
                    </Group>
                  </Group>
                }
                getTeethDescription={({ teeth }) =>
                  describeTeethSelection(
                    toToothNumbers(teeth),
                    [...new Set(teeth.map(({ value }) => value))].map(type => ({
                      type,
                      count: teeth.filter(({ value }) => value === type).length,
                    }))
                  )
                }
                willUnselectTooth={toothId =>
                  field.state.value.teeth.some(
                    tooth =>
                      toothId === tooth.tooth &&
                      tooth.value === ItemTypeEnum.bridgePontic
                  )
                }
                applyToothSelectionClass={tooth => {
                  return tooth.value === ItemTypeEnum.bridgePontic
                    ? "toothStroke"
                    : "toothFilled";
                }}
                onClick={(toothId, newGroups) => {
                  setTeethInError([]);
                  handleToothClick({
                    toothId,
                    selectedTeeth: field.state.value.teeth,
                    setSelectedTeeth: teeth =>
                      field.setValue(() => {
                        const formattedNewGroups = newGroups.map(newGroup =>
                          newGroup.map(newTooth => {
                            const existingTooth = teeth.find(
                              ({ tooth }) => tooth === newTooth
                            );

                            return (
                              existingTooth ?? {
                                tooth: newTooth,
                                value: ItemTypeEnum.abutment,
                              }
                            );
                          })
                        );

                        return {
                          ...field.state.value,
                          teeth,
                          groupedTeeth: formattedNewGroups,
                        };
                      }),
                  });
                }}
                onGroupedTeethChange={newGroups => {
                  setTeethInError([]);
                  field.setValue(prev => {
                    const formattedNewGroups = newGroups.map(newGroup =>
                      newGroup.map(newTooth => {
                        const existingTooth = prev.teeth.find(
                          ({ tooth }) => tooth === newTooth
                        );

                        return (
                          existingTooth ?? {
                            tooth: newTooth,
                            value: ItemTypeEnum.abutment,
                          }
                        );
                      })
                    );
                    return {
                      ...field.state.value,
                      groupedTeeth: formattedNewGroups,
                    };
                  });
                }}
              />
            );
          }}
        </form.Field>
      </Stack>
    </Stack>
  );
};
